/**
 * Portal Manta · SharePointPicker
 * Extraído de App.jsx (v29 modular)
 */
import React, { useState, useEffect, useMemo, useCallback, Fragment, memo } from 'react';
import { M, F, STATUS } from '../../tokens/theme.js';
import { SHAREPOINT_FAVS } from '../../data/sharepoint-favs.js';
import { SP_API } from '../../data/sp-api.js';
import { probeSharePointBackend } from '../../app-globals.js';

const SharePointPicker = memo(function SharePointPicker_Inner({onArquivoSelecionado, onErro}){
  const [backendStatus, setBackendStatus] = useState('probing'); // probing | on | off
  const [pastaAtiva, setPastaAtiva] = useState(SHAREPOINT_FAVS[0].id);
  const [urlCustom, setUrlCustom] = useState('');
  const [carregando, setCarregando] = useState(null);

  useEffect(() => { probeSharePointBackend(setBackendStatus); }, []);

  const pasta = SHAREPOINT_FAVS.find(p => p.id === pastaAtiva) || SHAREPOINT_FAVS[0];

  const tentarBaixar = async (urlCompleta, nomeArquivo) => {
    setCarregando(nomeArquivo || 'arquivo');
    if (backendStatus === 'on'){
      try {
        const data = await SP_API.fetchFile(urlCompleta);
        onArquivoSelecionado({nome: nomeArquivo, conteudo: JSON.stringify(data), url: urlCompleta, modo:'auto'});
      } catch(e){
        onErro({tipo:'backend-erro', msg:'Backend falhou: '+e.message+' · use modo manual abaixo', url:urlCompleta});
      }
    } else {
      onErro({tipo:'backend-off', msg:'Backend SharePoint OFF · siga as instruções abaixo', url:urlCompleta, nome:nomeArquivo});
    }
    setCarregando(null);
  };

  const validarUrlSharePoint = (u) => {
    if (!u) return null;
    if (u.includes('sharepoint.com')) return 'url-sp';
    if (u.startsWith('/personal/') || u.startsWith('/sites/')) return 'path-sp';
    if (u.startsWith('file:///')) return 'graph-uri';
    return null;
  };
  const tipoUrl = validarUrlSharePoint(urlCustom.trim());

  return (<div>
    {/* Status do backend */}
    <div style={{
      display:'flex', alignItems:'center', gap:10, padding:'10px 14px', marginBottom:14,
      background: backendStatus==='on'?'#F0FDF4':(backendStatus==='off'?'#FEF3C7':'#EFF6FF'),
      borderLeft:`3px solid ${backendStatus==='on'?M.green:(backendStatus==='off'?M.yellow:M.blue)}`,
      borderRadius:'0 4px 4px 0', fontSize:11,
    }}>
      <span style={{fontSize:14}}>{backendStatus==='on'?'🟢':(backendStatus==='off'?'🟡':'🔵')}</span>
      <div style={{flex:1}}>
        <b style={{color:M.heading}}>Backend SharePoint:</b>{' '}
        <span style={{color:backendStatus==='on'?M.green:(backendStatus==='off'?M.yellow:M.blue), fontWeight:700}}>
          {backendStatus==='on'?'ATIVO · download automático':(backendStatus==='off'?'INATIVO · modo manual ativado':'verificando…')}
        </span>
        <div style={{fontSize:9.5, color:M.muted, marginTop:2, fontFamily:F.mono}}>
          {backendStatus==='on'?`Endpoint: ${SP_API.base}/api/sharepoint/`:`Endpoint esperado: ${SP_API.base}/api/sharepoint/health (timeout 2s)`}
        </div>
      </div>
      <button onClick={()=>{setBackendStatus('probing'); probeSharePointBackend(setBackendStatus);}} style={{padding:'4px 10px', fontSize:9.5, fontWeight:700, border:`1px solid ${M.border}`, background:'#fff', color:M.muted, cursor:'pointer', borderRadius:3, fontFamily:F.body}}>↻ Testar</button>
    </div>

    {/* Pastas favoritas */}
    <div style={{fontSize:10, fontWeight:700, color:M.muted, letterSpacing:.5, textTransform:'uppercase', marginBottom:6}}>
      Pastas Manta favoritas
    </div>
    <div style={{display:'flex', gap:6, flexWrap:'wrap', marginBottom:10}}>
      {SHAREPOINT_FAVS.map(p => (
        <button key={p.id} onClick={()=>setPastaAtiva(p.id)} style={{
          padding:'6px 12px', fontSize:10, fontWeight:700, fontFamily:F.body,
          border:`1.5px solid ${pastaAtiva===p.id?M.accent:M.border}`,
          background: pastaAtiva===p.id?'#FDF0E8':'#fff',
          color: pastaAtiva===p.id?M.accent:M.text,
          borderRadius:4, cursor:'pointer',
          opacity: p.placeholder?0.6:1,
        }}>
          {p.placeholder?'⏳ ':''}{p.nome}
        </button>
      ))}
    </div>

    {/* Detalhes da pasta */}
    <div style={{padding:'10px 12px', background:M.bgAlt, borderRadius:5, marginBottom:14, fontSize:10.5}}>
      <div style={{color:M.muted, marginBottom:4}}>{pasta.descricao}</div>
      <div style={{fontFamily:F.mono, fontSize:9.5, color:M.heading, wordBreak:'break-all', marginBottom:6}}>
        📁 {pasta.path}
      </div>
      {pasta.url && (
        <a href={pasta.url} target="_blank" rel="noopener noreferrer" style={{fontSize:9.5, color:M.blue, textDecoration:'none'}}>
          🔗 Abrir no SharePoint Online
        </a>
      )}
    </div>

    {/* Arquivos da pasta */}
    {pasta.arquivos.length > 0 && (<>
      <div style={{fontSize:10, fontWeight:700, color:M.muted, letterSpacing:.5, textTransform:'uppercase', marginBottom:6}}>
        Arquivos disponíveis
      </div>
      <div style={{display:'flex', flexDirection:'column', gap:4, marginBottom:14}}>
        {pasta.arquivos.map(arq => {
          const urlCompleta = pasta.url ? pasta.url + arq : pasta.path + arq;
          return (
            <div key={arq} style={{display:'flex', alignItems:'center', gap:10, padding:'8px 12px', background:'#fff', border:`1px solid ${M.border}`, borderRadius:4}}>
              <span style={{fontSize:14}}>{arq.endsWith('.json')?'📋':(arq.endsWith('.pdf')?'📄':'📊')}</span>
              <span style={{flex:1, fontFamily:F.mono, fontSize:11, color:M.heading}}>{arq}</span>
              <button onClick={()=>tentarBaixar(urlCompleta, arq)} disabled={carregando===arq || pasta.placeholder} style={{
                padding:'4px 12px', fontSize:10, fontWeight:700, fontFamily:F.body,
                border:'none', borderRadius:3, cursor: pasta.placeholder?'not-allowed':'pointer',
                background: backendStatus==='on'?M.green:M.accent, color:'#fff',
                opacity:(carregando===arq || pasta.placeholder)?0.5:1,
              }}>
                {carregando===arq?'⏳':(backendStatus==='on'?'⬇ Baixar':'📋 Instruções')}
              </button>
            </div>
          );
        })}
      </div>
    </>)}

    {/* URL custom · cole link/path */}
    <div style={{borderTop:`1px solid ${M.border}`, paddingTop:12, marginTop:8}}>
      <div style={{fontSize:10, fontWeight:700, color:M.muted, letterSpacing:.5, textTransform:'uppercase', marginBottom:6}}>
        Ou cole URL/path do SharePoint
      </div>
      <div style={{display:'flex', gap:6}}>
        <input
          type="text"
          value={urlCustom}
          onChange={e=>setUrlCustom(e.target.value)}
          placeholder="https://mnassociados-my.sharepoint.com/.../arquivo.json  ou  /personal/.../arquivo.json"
          style={{flex:1, padding:'7px 10px', border:`1px solid ${M.border}`, borderRadius:4, fontFamily:F.mono, fontSize:10.5, color:M.text, outline:'none'}}
        />
        <button
          onClick={()=>{
            const u = urlCustom.trim();
            const nome = u.split('/').pop() || 'arquivo';
            tentarBaixar(u, nome);
          }}
          disabled={!tipoUrl}
          style={{
            padding:'7px 14px', fontSize:10.5, fontWeight:700, fontFamily:F.body,
            border:'none', borderRadius:4, cursor: tipoUrl?'pointer':'not-allowed',
            background: tipoUrl?M.accent:M.border, color: tipoUrl?'#fff':M.muted,
          }}
        >
          {backendStatus==='on'?'⬇ Baixar':'📋 Localizar'}
        </button>
      </div>
      {urlCustom && (
        <div style={{fontSize:9.5, marginTop:4, color: tipoUrl?M.green:M.red, fontFamily:F.mono}}>
          {tipoUrl?`✓ Reconhecido como ${tipoUrl}`:'✗ Formato não reconhecido (use https://...sharepoint.com... ou /personal/... ou /sites/...)'}
        </div>
      )}
    </div>
  </div>);
});

export { SharePointPicker };
export default SharePointPicker;
